import { createStore, select, withProps } from '@ngneat/elf';
import { withRequestsCache, withRequestsStatus } from '@ngneat/elf-requests';
import { Injectable } from '@angular/core';
import { RestService } from '../../../shared/services/rest.service';
import { AnimalDocumentModel } from '../models/animal-document.model';
import { BehaviorSubject, EMPTY, Observable, of } from 'rxjs';
import { tap, catchError, take, finalize, shareReplay, map, filter } from 'rxjs/operators';
import { TranslocoService } from '@jsverse/transloco';
import { ToastService } from 'src/app/shared/services/toast.service';
import { AnimalRepository } from './animal.repository';

export interface AnimalDocumentState {
  documents: Record<number, AnimalDocumentModel[]>;
}

export const store = createStore(
  { name: 'animalDocuments' },
  withProps<AnimalDocumentState>({ documents: {} }),
  withRequestsCache<'animalDocuments'>(),
  withRequestsStatus<'animalDocuments'>()
);

@Injectable({ providedIn: 'root' })
export class AnimalDocumentsRepository {
  private readonly _documentAddedSubject = new BehaviorSubject<AnimalDocumentModel | null>(null);
  private readonly documentsInFlight = new Map<number, Observable<AnimalDocumentModel[]>>();
  documentAdded$ = this._documentAddedSubject.asObservable();

  constructor(
    private readonly _restService: RestService,
    private readonly _translateService: TranslocoService,
    private readonly _toast: ToastService,
    private readonly _animalRepository: AnimalRepository
  ) {}

  getDocuments(animalId: number): Observable<AnimalDocumentModel[]> {
    if (!animalId) {
      return of([]);
    }

    // synchroner Cache-Check: liegt bereits etwas im Store, kein HTTP
    const cachedDocuments = store.getValue().documents[animalId] ?? null;
    if (cachedDocuments !== null) {
      return of(cachedDocuments);
    }

    // läuft bereits ein Request für dieses Tier, dessen geteiltes Observable zurückgeben
    const pending = this.documentsInFlight.get(animalId);
    if (pending) {
      return pending;
    }

    const request$ = this.fetchDocuments(animalId).pipe(
      catchError(() => of([] as AnimalDocumentModel[])),
      finalize(() => this.documentsInFlight.delete(animalId)),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.documentsInFlight.set(animalId, request$);
    return request$;
  }

  selectDocuments(animalId: number): Observable<AnimalDocumentModel[]> {
    return store.pipe(
      select(state => state.documents[animalId]),
      filter((documents): documents is AnimalDocumentModel[] => !!documents)
    );
  }

  // Lädt die Dokumente neu, z.B. nachdem das Backend eine Bericht-PDF erzeugt hat.
  // Ist für das Tier noch nichts geladen, holt der nächste getDocuments() ohnehin frisch.
  refreshDocuments(animalId: number): void {
    if (!animalId || !store.getValue().documents[animalId]) {
      return;
    }

    this.fetchDocuments(animalId)
      .pipe(take(1), catchError(() => EMPTY))
      .subscribe();
  }

  private fetchDocuments(animalId: number): Observable<AnimalDocumentModel[]> {
    return this._restService.httpGet(`/animal_documents?animal.id=${animalId}`).pipe(
      map(response => (Array.isArray(response) ? this.normalizeList(response, animalId) : [])),
      tap(documents => {
        store.update(state => ({
          ...state,
          documents: {
            ...state.documents,
            [animalId]: documents
          }
        }));
      })
    );
  }

  addDocument(document: FormData, animalId: number): Observable<AnimalDocumentModel> {
    return this._restService.httpPost('/animal_documents', document).pipe(
      map(raw => this.normalize(raw, animalId)),
      tap(normalized => {
        store.update(state => ({
          ...state,
          documents: {
            ...state.documents,
            [animalId]: [...(state.documents[animalId] || []), normalized]
          }
        }));

        this._documentAddedSubject.next(normalized);

        this._animalRepository.updateAnimalStore(animalId, {
          updatedAt: new Date(),
          hasFullData: false
        });
      })
    );
  }

  updateDocument(documentId: number, document: FormData, animalId: number): Observable<AnimalDocumentModel> {
    return this._restService.httpPut(`/animal_documents/${documentId}`, document).pipe(
      take(1),
      map(raw => this.normalize(raw, animalId)),
      tap(updated => {
        store.update(state => ({
          ...state,
          documents: {
            ...state.documents,
            [animalId]: state.documents[animalId]?.map(doc => doc.id === documentId ? { ...doc, ...updated } : doc) || []
          }
        }));
        this._toast.showSuccess(this._translateService.translate('toast.saveSuccess'));
      })
    );
  }

  deleteDocument(documentId: number, animalId: number): Observable<void> {
    return this._restService.httpDelete(`/animal_documents/${documentId}`).pipe(
      tap(() => {
        store.update(state => ({
          ...state,
          documents: {
            ...state.documents,
            [animalId]: state.documents[animalId]?.filter(doc => doc.id !== documentId) || []
          }
        }));
        this._animalRepository.updateAnimalStore(animalId, { updatedAt: new Date(), hasFullData: false });
      }),
      finalize(() => this._toast.showSuccess(this._translateService.translate('toast.deleteSuccess')))
    );
  }

  clearDocumentAdded(): void {
    this._documentAddedSubject.next(null);
  }

  private normalizeList(list: AnimalDocumentModel[], animalId: number): AnimalDocumentModel[] {
    return list.map(item => this.normalize(item, animalId));
  }

  private normalize(raw: any, animalId: number): AnimalDocumentModel {
    return {
      ...raw,
      animalId: raw.animal?.id ?? raw.animalId ?? animalId
    };
  }
}
